import React, { useState, useMemo, useEffect, useRef } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
} from "recharts";
import { Typography } from "@mui/material";
import { StabilityPresentation } from "../../../types/ui/weaponPresentation";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import HomeIcon from '@mui/icons-material/Home';
import EastIcon from '@mui/icons-material/East';
import WestIcon from '@mui/icons-material/West';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

interface Props {
    data: StabilityPresentation;
}

const StabilityChart: React.FC<Props> = ({ data }) => {
    const lineKeys = [
        { key: "sight_picture_angle", name: "Sight Picture Angle", color: "#FFD700" },
        { key: "rotation_speed", name: "Rotation Speed", color: "#007BFF" },
        { key: "convergence_green", name: "Convergence (Far)", color: "#E59D4B" },
        { key: "convergence_red", name: "Convergence (Near)", color: "#1DB0BF" },
    ];

    const eventsRaw = data?.repetition_events || [];
    const eventTypes = useMemo(() => Array.from(new Set(eventsRaw.map(e => e.event))), [eventsRaw]);

    const [visibleLines, setVisibleLines] = useState(() =>
        Object.fromEntries(lineKeys.map(l => [l.key, true]))
    );
    const [visibleEvents, setVisibleEvents] = useState(() =>
        Object.fromEntries(eventTypes.map(e => [e, true]))
    );

    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<{ x: number; domain: [number, number] } | null>(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const chartRef = useRef<HTMLDivElement>(null);

    const ZOOM_CONFIG = {
        MIN_ZOOM: 0.08,
        MAX_ZOOM: 147.4,
        ZOOM_FACTOR: 0.7,
        ZOOM_STEPS: [0.001, 0.01, 0.1, 0.5, 1, 2, 5, 10, 20, 50, 100],
    };

    const processedData = useMemo(() => {
        if (!data?.stability_plot?.length) {
            return null;
        }

        const startTime = data.stability_plot[0].time;
        const formatted = data.stability_plot.map(p => ({
            time: (p.time - startTime) / 1000,
            sight_picture_angle: p.sight_picture_angle,
            rotation_speed: p.rotation_speed,
            convergence_green: p.convergence === 1 ? 1 : null,
            convergence_red: p.convergence === 0 ? 1 : null,
        }));

        const events = data.repetition_events.map(e => ({
            ...e,
            time: (e.time - startTime) / 1000,
        }));

        return { formatted, events, startTime };
    }, [data]);

    const fullDomain: [number, number] = useMemo(() => {
        if (!processedData) {
            return [0, 1];
        }

        const { formatted, events } = processedData;
        return [
            Math.floor(Math.min(formatted[0].time, ...events.map(e => e.time))),
            Math.ceil(Math.max(formatted[formatted.length - 1].time, ...events.map(e => e.time))),
        ];
    }, [processedData]);

    const [xDomain, setXDomain] = useState<[number, number]>(fullDomain);

    useEffect(() => {
        setXDomain(fullDomain);
    }, [fullDomain[0], fullDomain[1]]);

    useEffect(() => {
        console.log('xDomain changed:', {
            xDomain: [xDomain[0].toFixed(3), xDomain[1].toFixed(3)],
            range: (xDomain[1] - xDomain[0]).toFixed(3),
            fullDomain: [fullDomain[0].toFixed(3), fullDomain[1].toFixed(3)]
        });
    }, [xDomain, fullDomain]);

    useEffect(() => {
        const chartElement = chartRef.current;
        if (!chartElement) return;

        const wheelHandler = (event: WheelEvent) => {
            event.preventDefault();
            event.stopPropagation();

            const rect = chartElement.getBoundingClientRect();
            const mouseX = event.clientX - rect.left;
            const chartWidth = rect.width;

            const [min, max] = xDomain;
            const timeAtMouse = min + (mouseX / chartWidth) * (max - min);

            const direction = event.deltaY > 0 ? "out" : "in";
            handleZoom(direction, timeAtMouse);
        };

        chartElement.addEventListener('wheel', wheelHandler, { passive: false });

        return () => {
            chartElement.removeEventListener('wheel', wheelHandler);
        };
    }, [xDomain]);

    const handleLegendClick = (o: any) => {
        const { dataKey } = o;
        if (visibleLines.hasOwnProperty(dataKey)) {
            setVisibleLines(prev => ({ ...prev, [dataKey]: !prev[dataKey] }));
        } else if (visibleEvents.hasOwnProperty(dataKey)) {
            setVisibleEvents(prev => ({ ...prev, [dataKey]: !prev[dataKey] }));
        }
    };

    const EVENT_LABELS: Record<string, string> = {
        valid_shot: "Valid Shot",
        non_valid_shot: "Non Valid Shot",
        buzzer_start: "Buzzer Start",
        buzzer_end: "Buzzer End",
    };
    const EVENT_COLORS: Record<string, string> = {
        valid_shot: '#3ec46d',
        non_valid_shot: '#e74c3c',
        buzzer_start: '#ccc',
        buzzer_end: '#ccc',
    };
    const renderLineToggle = (l: typeof lineKeys[number] | undefined) => {
        if (!l) return null;
        return (
            <span
                key={l.key}
                onClick={() => handleLegendClick({ dataKey: l.key })}
                style={{
                    color: visibleLines[l.key] ? l.color : '#888',
                    textDecoration: visibleLines[l.key] ? 'none' : 'line-through',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    opacity: visibleLines[l.key] ? 1 : 0.5,
                    minHeight: 28,
                }}
            >
                <svg width="14" height="14" style={{ marginRight: 4 }}>
                    <rect width="14" height="14" fill={l.color} />
                </svg>
                {l.name}
            </span>
        );
    };
    const renderEventToggle = (ev: string) => (
        <span
            key={ev}
            onClick={() => handleLegendClick({ dataKey: ev })}
            style={{
                color: visibleEvents[ev] ? EVENT_COLORS[ev] || '#ccc' : '#888',
                textDecoration: visibleEvents[ev] ? 'none' : 'line-through',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                opacity: visibleEvents[ev] ? 1 : 0.5,
                minHeight: 28,
            }}
        >
            <svg width="14" height="14" style={{ marginRight: 4 }}>
                <rect width="14" height="14" fill={EVENT_COLORS[ev] || '#ccc'} />
            </svg>
            {EVENT_LABELS[ev] || ev.replace(/_/g, ' ')}
        </span>
    );
    const GraphDataToggles = (
        <Grid
            container
            spacing={1}
            sx={{
                width: 'fit-content', // ⬅️ forces grid to shrink around its children
            }}
        >
            <Grid item xs={4}>
                {renderLineToggle(lineKeys.find(x => x.key === 'sight_picture_angle'))}
            </Grid>
            <Grid item xs={6}>
                {renderLineToggle(lineKeys.find(x => x.key === 'convergence_green'))}
            </Grid>
            <Grid item xs={4}>
                {renderLineToggle(lineKeys.find(x => x.key === 'rotation_speed'))}
            </Grid>
            <Grid item xs={6}>
                {renderLineToggle(lineKeys.find(x => x.key === 'convergence_red'))}
            </Grid>
        </Grid>
    );

    // MUI Grid for Events (2x2)
    const EventsToggles = (
        <Grid
            container
            spacing={1}
            sx={{
                width: 'fit-content', // force minimal width
                justifyContent: 'flex-end', // align items to the right
            }}
        >
            <Grid item xs={6}>
                {renderEventToggle('buzzer_start')}
            </Grid>
            <Grid item xs={4}>
                {renderEventToggle('buzzer_end')}
            </Grid>
            <Grid item xs={6}>
                {renderEventToggle('valid_shot')}
            </Grid>
            <Grid item xs={4}>
                {renderEventToggle('non_valid_shot')}
            </Grid>
        </Grid>
    );

    const getXTicks = (min: number, max: number, approxCount = 5): number[] => {
        const range = max - min;
        if (range <= 0) return [min];
        const roughStep = range / approxCount;
        const pow10 = Math.pow(10, Math.floor(Math.log10(roughStep)));
        let step = pow10;
        if (roughStep / pow10 < 2) step = pow10 / 5;
        else if (roughStep / pow10 < 5) step = pow10 / 2;
        const ticks = [];
        let tick = Math.ceil(min / step) * step;
        while (tick <= max) {
            ticks.push(Number(tick.toFixed(3)));
            tick += step;
        }
        return ticks;
    };

    const xTicks = useMemo(() => {
        if (!xDomain || xDomain.length !== 2) return [];
        return getXTicks(xDomain[0], xDomain[1]);
    }, [xDomain]);
    const filteredData = useMemo(() => {
        if (!processedData || !processedData.formatted) return [];
        return processedData.formatted.filter(
            (point) => point.time >= xDomain[0] && point.time <= xDomain[1]
        );
    }, [processedData, xDomain]);

    const yTicks = useMemo(() => {
        if (!filteredData.length) return [];
        const maxY = Math.max(
            ...filteredData.map(
                p =>
                    Math.max(
                        p.sight_picture_angle || 0,
                        p.rotation_speed || 0
                    )
            )
        );
        const step = 0.4;
        const ticks = [];
        for (let i = 0; i <= maxY + step; i += step) {
            ticks.push(+i.toFixed(1));
        }
        return ticks;
    }, [filteredData]);

    if (!processedData) {
        return <Typography color="gray">No stability data available</Typography>;
    }

    const { formatted, events } = processedData;

    const handlePan = (direction: "left" | "right") => {
        const [min, max] = xDomain;
        const shift = (max - min) * 0.2;
        let newMin, newMax;
        if (direction === "left") {
            newMin = Math.max(min - shift, fullDomain[0]);
            newMax = newMin + (max - min);
        } else {
            newMax = Math.min(max + shift, fullDomain[1]);
            newMin = newMax - (max - min);
        }
        if (newMin < fullDomain[0]) {
            newMin = fullDomain[0];
            newMax = newMin + (max - min);
        }
        if (newMax > fullDomain[1]) {
            newMax = fullDomain[1];
            newMin = newMax - (max - min);
        }
        setXDomain([newMin, newMax]);
    };

    const handleZoom = (direction: "in" | "out", zoomCenter?: number, zoomAmount?: number) => {
        const [min, max] = xDomain;
        const range = max - min;
        const center = zoomCenter !== undefined ? zoomCenter : (min + max) / 2;
        const zoomFactor = zoomAmount || (direction === "in" ? ZOOM_CONFIG.ZOOM_FACTOR : 1 / ZOOM_CONFIG.ZOOM_FACTOR);
        const newRange = range * zoomFactor;
        const fullRange = fullDomain[1] - fullDomain[0];
        if (direction === "in" && newRange < ZOOM_CONFIG.MIN_ZOOM) {
            return;
        }
        let newMin = Math.max(center - newRange / 2, fullDomain[0]);
        let newMax = Math.min(center + newRange / 2, fullDomain[1]);
        if (direction === "out" && (newMax - newMin >= fullRange - 1e-6)) {
            newMin = fullDomain[0];
            newMax = fullDomain[1];
        }

        setXDomain([newMin, newMax]);
        setZoomLevel(fullRange / (newMax - newMin));
    };
    const handleMouseDown = (event: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart({
            x: event.clientX,
            domain: xDomain
        });
    };
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || !payload.length) return null;

        const convergenceEntry = payload.find((entry: any) =>
            entry.dataKey === "convergence_green" || entry.dataKey === "convergence_red"
        );

        return (
            <div
                style={{
                    backgroundColor: '#111',
                    padding: '10px',
                    borderRadius: '5px',
                    border: '1px solid #444',
                    color: '#fff',
                    fontSize: 13,
                    boxShadow: '0 0 6px rgba(0,0,0,0.4)',
                }}
            >
                <p style={{ marginBottom: 6, fontWeight: 600 }}>{label.toFixed(2)} s</p>
                {payload.map((entry: any, i: number) => {
                    if (entry.dataKey === "convergence_green" || entry.dataKey === "convergence_red") {
                        return null; // skip individual convergence lines
                    }

                    return (
                        <p key={i} style={{ color: entry.color, margin: 0 }}>
                            {entry.name}: {Number(entry.value).toFixed(2)}
                        </p>
                    );
                })}

                {convergenceEntry?.dataKey === "convergence_green" && convergenceEntry.value === 1 && (
                    <p style={{ color: convergenceEntry.color, margin: 0 }}>Convergence: Far</p>
                )}
                {convergenceEntry?.dataKey === "convergence_red" && convergenceEntry.value === 1 && (
                    <p style={{ color: convergenceEntry.color, margin: 0 }}>Convergence: Near</p>
                )}
            </div>
        );
    };


    const handleMouseMove = (event: React.MouseEvent) => {
        if (!isDragging || !dragStart || !chartRef.current) return;

        const rect = chartRef.current.getBoundingClientRect();
        const deltaX = event.clientX - dragStart.x;
        const chartWidth = rect.width;
        const [min, max] = dragStart.domain;
        const timeRange = max - min;
        const timeShift = -(deltaX / chartWidth) * timeRange;

        const newMin = Math.max(min + timeShift, fullDomain[0]);
        const newMax = Math.min(max + timeShift, fullDomain[1]);

        setXDomain([newMin, newMax]);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setDragStart(null);
    };

    const buttonStyle = {
        padding: '8px 12px',
        margin: '0 2px',
        backgroundColor: 'transparent',
        color: '#fff',
        border: '1px solid #555',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
    };

    const buttonHoverStyle = {
        backgroundColor: '#1c1c1c',
        borderColor: '#888',
    };

    return (
        <Box>
            {/* Pan & Zoom Buttons */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                mb={2}
                gap={1}
                minHeight={48}
            >
                <button
                    onClick={() => handlePan("left")}
                    style={buttonStyle}
                    onMouseOver={(e) => Object.assign(e.currentTarget.style, buttonHoverStyle)}
                    onMouseOut={(e) => Object.assign(e.currentTarget.style, { backgroundColor: "#2c3e50" })}
                >
                    <WestIcon />
                </button>
                <button
                    onClick={() => handleZoom("in")}
                    style={buttonStyle}
                    onMouseOver={(e) => Object.assign(e.currentTarget.style, buttonHoverStyle)}
                    onMouseOut={(e) => Object.assign(e.currentTarget.style, { backgroundColor: "#2c3e50" })}
                >
                    <AddIcon />
                </button>
                <button
                    onClick={() => handleZoom("out")}
                    style={buttonStyle}
                    onMouseOver={(e) => Object.assign(e.currentTarget.style, buttonHoverStyle)}
                    onMouseOut={(e) => Object.assign(e.currentTarget.style, { backgroundColor: "#2c3e50" })}
                >
                    <RemoveIcon />
                </button>
                <button
                    onClick={() => handlePan("right")}
                    style={buttonStyle}
                    onMouseOver={(e) => Object.assign(e.currentTarget.style, buttonHoverStyle)}
                    onMouseOut={(e) => Object.assign(e.currentTarget.style, { backgroundColor: "#2c3e50" })}
                >
                    <EastIcon />
                </button>
                <button
                    onClick={() => {
                        setXDomain(fullDomain);
                        setZoomLevel(1);
                    }}
                    style={{
                        ...buttonStyle,
                        fontSize: "18px",
                        padding: "7px 12px",
                        opacity:
                            xDomain[0] === fullDomain[0] && xDomain[1] === fullDomain[1] ? 0.4 : 1,
                        cursor:
                            xDomain[0] === fullDomain[0] && xDomain[1] === fullDomain[1]
                                ? "not-allowed"
                                : "pointer",
                    }}
                    title="Reset View"
                    disabled={xDomain[0] === fullDomain[0] && xDomain[1] === fullDomain[1]}
                    onMouseOver={(e) => Object.assign(e.currentTarget.style, buttonHoverStyle)}
                    onMouseOut={(e) => Object.assign(e.currentTarget.style, { backgroundColor: "#2c3e50" })}
                >
                    <HomeIcon />
                </button>
            </Box>

            {/* Chart */}
            <Box
                ref={chartRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                sx={{
                    cursor: isDragging ? "grabbing" : "grab",
                    userSelect: "none",
                    minHeight: 400,
                    position: "relative",
                }}
            >
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={filteredData} margin={{ top: 10, right: 50, bottom: 60, left: 60 }}>
                        <XAxis
                            dataKey="time"
                            stroke="#ccc"
                            type="number"
                            tickFormatter={(value) => `${value.toFixed(2)}`}
                            domain={xDomain}
                            ticks={xTicks}
                            label={{
                                value: "Time (s)",
                                position: "insideBottomRight",
                                offset: -10,
                                
                                fontSize: 18,
                                dx: -500,
                                dy:20
                            }}
                        />
                        <YAxis
                            stroke="#ccc"
                            domain={[0, 'auto']}
                            tickFormatter={(v) => v.toFixed(1)}
                            ticks={yTicks}
                            label={{ value: "Angle (°)", angle: -90, position: "insideLeft", dx: -15, dy: 35 }}
                        />
                        <Tooltip content={<CustomTooltip />} />

                        {lineKeys.map((l) =>
                            visibleLines[l.key] ? (
                                <Line
                                    key={l.key}
                                    type={l.key.startsWith("convergence") ? "stepAfter" : "monotone"}
                                    dataKey={l.key}
                                    stroke={l.color}
                                    name={l.name}
                                    dot={false}
                                    strokeWidth={l.key.startsWith("convergence") ? 3 : 2}
                                />
                            ) : null
                        )}

                        {events.map((e, idx) =>
                            visibleEvents[e.event] ? (
                                <ReferenceLine
                                    key={idx}
                                    x={e.time}
                                    stroke={EVENT_COLORS[e.event] || "#999"}
                                    strokeDasharray={e.event.includes("buzzer") ? "4 4" : "1 1"}
                                    strokeWidth={2}
                                    label={
                                        e.event === "valid_shot" || e.event === "non_valid_shot"
                                            ? undefined
                                            : {
                                                value: EVENT_LABELS[e.event] || e.event.replace(/_/g, " "),
                                                angle: -90,
                                                position: "right",
                                                fontSize: 12,
                                                dy:70,
                                                dx:5,
                                                fill: EVENT_COLORS[e.event] || "#ccc",
                                            }
                                    }
                                />
                            ) : null
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </Box>

            {/* Bottom: Toggles */}
            <Box
                mt={1}
                display="flex"
                width="100%"
                gap={3}
                flexWrap="wrap"

            >
                {/* Left: Graph Data */}
                <Box minWidth={240} >
                    <Typography
                        variant="subtitle2"
                        color="#fff"
                        gutterBottom
                        sx={{  textAlign:'center',paddingRight:'15rem'}}
                    >
                        Graph Data
                    </Typography>

                    {GraphDataToggles}
                </Box>




                {/* Right: Event Markers */}
                <Box
                    sx={{
                        minWidth: 240,
                        ml: 'auto',
                        display: 'flex',
                        flexDirection: 'column',

                    }}
                >
                    <Typography variant="subtitle2" color="#fff" gutterBottom alignItems="center">
                        Event Markers
                    </Typography>
                    {EventsToggles}
                </Box>

            </Box>


        </Box>
    );

};

export default StabilityChart;
